import { FeedbackSessionControllerClass } from '../feedback-session.controller';
import { FeedbackBaseModelClass } from 'go-modules/models/base/base.factory';
import { FeedbackSessionComponentScope } from '../feedback-session.component';
import { EventService } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { ResponsiveViewService } from 'go-modules/responsive-view/responsive-view.service';
import { CommentListService } from '@go-modules/services/comment-list/comment-list.service';
import { FeedbackSessionService } from '@go-modules/services/feedback-session/feedback-session.service';

export interface Bindings {
	readonly mainController: FeedbackSessionControllerClass;
}

export interface Locals {
	feedbackSession: any;
	mainController: FeedbackSessionControllerClass;
}

export interface FeedbackSessionContentControllerScope extends FeedbackSessionComponentScope, Locals {}

export declare class FeedbackSessionContentControllerClass implements ng.IController, Bindings {
	public readonly mainController: FeedbackSessionControllerClass;
	public settings: any;
	public eventNames: string[];
	public isMobileView: boolean;

	public $onInit (): void;
	public $onDestroy(): void;
	public addFeedbackItem (feedbackItem: FeedbackBaseModelClass): void;
	public removeFeedbackItem (feedbackItem: FeedbackBaseModelClass): void;
	public getFeedback (): ng.IPromise<{[type: string]: FeedbackBaseModelClass[]}>;
	public isInAsyncSlideEditMode (): boolean;
	public removeSyncEventComments (): void;
	public saveFeedbackItem (feedbackItem: FeedbackBaseModelClass): ng.IPromise<void>;
	public shouldDisplayMediaPane (): boolean;
	public shouldDisplayFeedbackDisplayPanel (): boolean;
	public isMobileSize(): boolean;
	public getMobileSize(): boolean;
	public getMinSize(): number;
}

/* @ngInject */
export function FeedbackSessionContentController (
	$scope: FeedbackSessionContentControllerScope,
	feedbackSession,
	eventService: EventService,
	responsiveView: ResponsiveViewService,
	ngxCommentListService: CommentListService,
	ngxFeedbackSessionService: FeedbackSessionService
) {
	const vm: FeedbackSessionContentControllerClass = this;

	/**
	 * Controller init handler
	 */
	vm.$onInit = () => {
		vm.settings = feedbackSession;

		// TODO: Expose these controllers on the scope until we refactor the feedback-creator, etc...
		$scope.mainController = vm.mainController;
		$scope.feedbackSession = feedbackSession;

		// Close panel if mobile
		vm.isMobileSize();

		// Immediately get all feedback
		vm.getFeedback();
	};

	vm.$onDestroy = () => {
		ngxCommentListService.setComments([]);
	};

	/**
	 * Get the feedback for this session
	 *
	 * @returns {Object}
	 */
	vm.getFeedback = () => {
		const promise = vm.mainController.sessionFeedback.get();
		promise.then((feedback) => {
			eventService.broadcast(EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_LOADED, feedback);
		});

		return promise;
	};

	/**
	 * Add feedback item to session
	 *
	 * @param feedbackItem
	 */
	vm.addFeedbackItem = (feedbackItem) => {
		vm.mainController.sessionFeedback.add(feedbackItem);
	};

	/**
	 * Remove feedback item from session (still used for rubrics)
	 *
	 * @param feedbackItem
	 */
	vm.removeFeedbackItem = (feedbackItem): void => {
		vm.mainController.sessionFeedback.remove(feedbackItem);
	};

	/**
	 * Save feedback item
	 *
	 * @param feedbackItem
	 */
	vm.saveFeedbackItem = (feedbackItem: FeedbackBaseModelClass): ng.IPromise<void> => {
		return vm.mainController.sessionFeedback.save(feedbackItem).then((item) => {
			ngxCommentListService.addComment(item as any);
		});
	};

	/**
	 * Remove all comments of a given type
	 *
	 * @param type
	 * @returns {*}
	 */
	vm.removeSyncEventComments = () => {
		const commentsToDelete = ngxCommentListService.currentComments.filter((item: any) => {
			return item.isSyncEventType();
		});
		commentsToDelete.forEach((comment) => {
			ngxCommentListService.removeComment(comment);
			comment.delete();
		});
	};

	/**
	 * Whether we are in async slide edit mode
	 */
	vm.isInAsyncSlideEditMode = () => {
		return vm.mainController.playerSync &&
			vm.mainController.playerSync.isCaptureMode() &&
			vm.mainController.sourceMedia &&
			vm.mainController.sourceMedia.isDocument() &&
			!vm.mainController.activity.isLiveSessionEnabled() &&
			vm.mainController.activity.isSlidesEnabled();
	};

	/**
	 * Hide media pane when there is no media to speak of
	 * and the activity says there is no response media
	 */
	vm.shouldDisplayMediaPane = (): boolean => {
		return vm.mainController.hasOrRequiresResponseMedia();
	};

	vm.shouldDisplayFeedbackDisplayPanel = (): boolean => {
		return !vm.isInAsyncSlideEditMode() &&
			!vm.mainController.activity.isConversationBoard();
	};

	vm.isMobileSize = (): boolean => {
		const view = responsiveView.showView('FEEDBACK_PANEL_MOBILE_VIEW');
		if (view && this.isMobileView !== view && !vm.mainController.activity.isConversationBoard()) {
			ngxFeedbackSessionService.togglePanel(false);
		}
		this.isMobileView = view;
		$scope.$evalAsync();
		return this.isMobileView;
	};

	vm.getMobileSize = (): boolean => {
		return responsiveView.showView('FEEDBACK_PANEL_MOBILE_VIEW');
	};

	vm.getMinSize = (): number => {
		if (responsiveView.showView('FEEDBACK_PANEL_MOBILE_VIEW')) {
			return 280;
		}
		return 380;
	};
}
